/**
 * Constants for the WirelessAdvisor component
 */

// Form steps
export const FORM_STEPS = [
  'size',
  'routerLocation',
  'rooms',
  'layout',
  'coverage',
  'internetSpeed',
  'generating',
  'results'
];

// Default form data
export const DEFAULT_FORM_DATA = {
  homeSize: '',
  area: 100,
  floors: 1,
  routerLocation: '',
  customRouterLocation: '',
  wallDensity: 1,
  constructionType: 'standard',
  rooms: {
    livingRoom: 1,
    bedroom: 2,
    kitchen: 1,
    bathroom: 1,
    office: 0,
    diningRoom: 0,
    gameRoom: 0,
    otherRoom: 0
  },
  priorityRooms: ['livingRoom', 'office'],
  internetSpeed: 'medium'
};

// Default recommendation
export const DEFAULT_RECOMMENDATION = {
  routerStandard: 'WIFI_6',
  routerSpecs: {},
  meshSystem: {
    recommendedUnits: 1,
    coverageLevels: {
      basic: { units: 1, placements: [] },
      medium: { units: 2, placements: [] },
      high: { units: 3, placements: [] }
    }
  },
  coverage: {
    highSpeed: 0,
    mediumSpeed: 0,
    lowSpeed: 0
  },
  coveragePercentages: {
    high: 0,
    medium: 0,
    low: 0
  },
  speedEstimates: {
    high: { download: 0, upload: 0, latency: 'Low' },
    medium: { download: 0, upload: 0, latency: 'Medium' },
    low: { download: 0, upload: 0, latency: 'High' }
  },
  notes: []
};

// Wireless specifications
export const WIRELESS_SPECS = {
  // Router specifications
  ROUTER_SPECS: {
    ROUTER_STANDARDS: {
      WIFI_5: {
        LABEL: 'WiFi 5 (802.11ac)',
        MAX_SPEED: 1300, // Mbps
        RANGE: 100, // meters
        REAL_WORLD_FACTOR: 0.6, // Real-world performance factor
        BANDS: ['2.4GHz', '5GHz'],
        FEATURES: [
          'MU-MIMO',
          'Beamforming'
        ]
      },
      WIFI_6: {
        LABEL: 'WiFi 6 (802.11ax)',
        MAX_SPEED: 2400, // Mbps
        RANGE: 120, // meters
        REAL_WORLD_FACTOR: 0.7, // Real-world performance factor
        BANDS: ['2.4GHz', '5GHz'],
        FEATURES: [
          'MU-MIMO',
          'OFDMA',
          'Beamforming',
          'BSS Coloring'
        ]
      },
      WIFI_6E: {
        LABEL: 'WiFi 6E (802.11ax)',
        MAX_SPEED: 3600, // Mbps
        RANGE: 130, // meters
        REAL_WORLD_FACTOR: 0.7, // Real-world performance factor
        BANDS: ['2.4GHz', '5GHz', '6GHz'],
        FEATURES: [
          'MU-MIMO',
          'OFDMA',
          'Beamforming',
          'BSS Coloring',
          '6GHz Band'
        ]
      },
      WIFI_7: {
        LABEL: 'WiFi 7 (802.11be)',
        MAX_SPEED: 5800, // Mbps
        RANGE: 150, // meters
        REAL_WORLD_FACTOR: 0.8, // Real-world performance factor
        BANDS: ['2.4GHz', '5GHz', '6GHz'],
        FEATURES: [
          'MU-MIMO',
          'OFDMA',
          'Beamforming',
          'BSS Coloring',
          '6GHz Band',
          'Multi-Link Operation',
          '320MHz Channels'
        ]
      }
    }
  },
  
  // Wall density factors
  WALL_DENSITY_FACTORS: {
    0: 1.0, // No walls
    1: 0.8, // Few walls
    2: 0.6, // Moderate walls
    3: 0.4  // Many walls
  },
  
  // Construction type factors
  CONSTRUCTION_TYPE_FACTORS: {
    'wood': 0.9,
    'standard': 0.7,
    'concrete': 0.5,
    'metal': 0.3
  },
  
  // Home size factors
  HOME_SIZE_FACTORS: {
    'XSMALL': 0.8,
    'SMALL': 0.9,
    'MEDIUM': 1.0,
    'LARGE': 1.1,
    'XLARGE': 1.2
  },
  
  // Home size to area mapping (in square meters)
  HOME_SIZE_TO_AREA: {
    'XSMALL': 50,
    'SMALL': 100,
    'MEDIUM': 150,
    'LARGE': 200,
    'XLARGE': 300
  },
  
  // Internet speed to Mbps mapping
  INTERNET_SPEED_TO_MBPS: {
    'slow': 50,
    'medium': 200,
    'fast': 500,
    'ultrafast': 1000
  },
  
  // Home sizes with details
  HOME_SIZES: {
    'XSMALL': {
      label: 'Extra Small Home',
      min: 0,
      max: 50,
      description: 'Apartment or small house'
    },
    'SMALL': {
      label: 'Small Home',
      min: 50,
      max: 100,
      description: 'Small house or apartment'
    },
    'MEDIUM': {
      label: 'Medium Home',
      min: 100,
      max: 150,
      description: 'Average-sized house'
    },
    'LARGE': {
      label: 'Large Home',
      min: 150,
      max: 200,
      description: 'Large house'
    },
    'XLARGE': {
      label: 'Extra Large Home',
      min: 200,
      max: 500,
      description: 'Very large house or mansion'
    }
  }
};

// Coverage level descriptions
export const COVERAGE_LEVEL_DESCRIPTIONS = {
  high: 'High-Speed Coverage',
  medium: 'Medium-Speed Coverage',
  low: 'Basic Coverage'
};

// Room type descriptions
export const ROOM_TYPE_DESCRIPTIONS = {
  livingRoom: 'Living Room',
  bedroom: 'Bedroom',
  kitchen: 'Kitchen',
  bathroom: 'Bathroom',
  office: 'Home Office',
  diningRoom: 'Dining Room',
  gameRoom: 'Game Room',
  otherRoom: 'Other Room'
};

// Home size descriptions
export const HOME_SIZE_DESCRIPTIONS = {
  XSMALL: 'Extra Small (< 50m²)',
  SMALL: 'Small (50-100m²)',
  MEDIUM: 'Medium (100-150m²)',
  LARGE: 'Large (150-200m²)',
  XLARGE: 'Extra Large (> 200m²)'
};

// Internet speed descriptions
export const INTERNET_SPEED_DESCRIPTIONS = {
  slow: 'Basic (< 50 Mbps)',
  medium: 'Standard (50-200 Mbps)',
  fast: 'Fast (200-500 Mbps)',
  ultrafast: 'Ultra-Fast (500+ Mbps)'
};

// Construction type descriptions
export const CONSTRUCTION_TYPE_DESCRIPTIONS = {
  wood: 'Wood Frame',
  standard: 'Standard Construction',
  concrete: 'Concrete/Brick',
  metal: 'Metal/Steel Frame'
};